/**
 * ServicePausePage.js — Toggle CEO/Provider CU para pausar el servicio de
 * remesas a nivel global o por moneda destino. Solo bloquea la CREACIÓN de
 * nuevas remesas (las en curso siguen).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pause, Play, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCIES = ['CUP', 'MLC', 'USD'];

const ServicePausePage = () => {
  const { getAuthHeaders } = useAuth();
  const [state, setState] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/service-pause`, { headers: getAuthHeaders() });
      setState(r.data);
      setNote(r.data?.note || '');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo cargar el estado'));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/service-pause`, patch, { headers: getAuthHeaders() });
      setState(r.data);
      toast.success('Estado actualizado');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'No se pudo guardar'));
    } finally { setBusy(false); }
  };

  if (!state) return <p className="text-center text-slate-500 py-12">Cargando…</p>;

  const isCcyPaused = (c) => (state.paused_currencies || []).includes(c);
  const toggleCcy = (c) => {
    const set = new Set(state.paused_currencies || []);
    if (set.has(c)) set.delete(c); else set.add(c);
    save({ paused_currencies: [...set] });
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="service-pause-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Pause className="w-6 h-6 text-amber-600" />
          Pausa de Servicios
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Pausa la creación de nuevas remesas para evitar sobregiros. Las remesas en curso siguen su flujo normal.
        </p>
      </div>

      <Card className={`p-6 border-0 shadow-sm ${state.is_paused_global ? 'bg-rose-50 border border-rose-200' : 'bg-white'}`} data-testid="global-pause-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              {state.is_paused_global ? <AlertTriangle className="w-5 h-5 text-rose-600" /> : <Play className="w-5 h-5 text-emerald-600" />}
              Servicio Global
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              {state.is_paused_global ? '🛑 Todas las remesas nuevas están bloqueadas.' : '✅ El servicio está online — los gestores pueden enviar.'}
            </p>
          </div>
          <Switch
            checked={state.is_paused_global}
            disabled={busy}
            onCheckedChange={(v) => save({ is_paused_global: v })}
            data-testid="global-pause-switch"
          />
        </div>
      </Card>

      <Card className="p-6 border-0 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-3">Pausa por moneda destino</h3>
        <p className="text-xs text-slate-500 mb-4">Pausa individual de cada moneda — útil cuando falta liquidez en una sola.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {CURRENCIES.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => toggleCcy(c)}
              disabled={busy}
              className={`p-4 rounded-lg border-2 transition text-left ${isCcyPaused(c) ? 'border-rose-300 bg-rose-50' : 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50'}`}
              data-testid={`ccy-pause-${c}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold text-slate-900">🇨🇺 {c}</span>
                {isCcyPaused(c)
                  ? <span className="text-[10px] uppercase bg-rose-200 text-rose-800 px-2 py-0.5 rounded-full font-bold">Pausado</span>
                  : <span className="text-[10px] uppercase bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full font-bold">Activo</span>}
              </div>
              <p className="text-xs text-slate-500">
                {isCcyPaused(c) ? 'No se pueden crear remesas nuevas a esta moneda.' : 'Click para pausar nuevas remesas a esta moneda.'}
              </p>
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-0 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-2">Mensaje visible para los gestores</h3>
        <p className="text-xs text-slate-500 mb-3">Aparece en la página de envío cuando intentan crear una remesa bloqueada.</p>
        <div className="flex gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej: Mantenimiento programado · vuelve a las 19h" data-testid="pause-note-input" />
          <Button onClick={() => save({ note })} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-note-btn">
            Guardar
          </Button>
        </div>
      </Card>

      <p className="text-[10px] text-slate-400 text-right">
        Última actualización: {state.updated_at ? new Date(state.updated_at).toLocaleString('es-ES') : '—'}
      </p>
    </div>
  );
};

export default ServicePausePage;
